import Vue from "vue";

// Helper function to send email via cloud function
async function sendEmail(toEmail, subject, html, text, fromName = 'XMBL', fromEmail = 'agent@xmbl.org') {
  const store = window.$store;
  const cloudurl = store.state.cloudurl || 'https://us-central1-xmba-cloud-api.cloudfunctions.net/';
  
  try {
    const response = await fetch(cloudurl + 'sendmail', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Origin': window.location.origin
      },
      body: JSON.stringify({
        subject: subject,
        from: {
          name: fromName,
          address: fromEmail
        },
        to: [{
          name: '',
          address: toEmail
        }],
        html: html,
        text: text
      })
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      // Only log actual email sending errors, not Firestore logging errors
      if (response.status >= 500) {
        console.error('Email send error:', error);
        return false;
      }
      // For 4xx errors, still log but return false
      console.log('Email send warning (non-critical):', error);
      return false;
    }
    // Success - email was sent
    return true;
  } catch (error) {
    // Don't show errors for network issues if email might have been sent
    console.error('Error sending email:', error);
    return false;
  }
}

// Helper function to format order details for email
function formatOrderDetails(items, shipping, payment) {
  let details = '<table style="width: 100%; border-collapse: collapse; margin: 20px 0;">';
  details += '<thead><tr style="background-color: #f5f5f5;"><th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Item</th><th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Qty</th><th style="padding: 10px; text-align: right; border: 1px solid #ddd;">Price</th><th style="padding: 10px; text-align: right; border: 1px solid #ddd;">Subtotal</th></tr></thead>';
  details += '<tbody>';
  
  items.forEach(item => {
    const itemCost = parseFloat(item.purchase.cost) || 0;
    const shippingCost = parseFloat(item.purchase["shipping and handling"]) || 0;
    const subtotal = (itemCost * item.qty) + shippingCost;
    
    details += '<tr>';
    details += `<td style="padding: 10px; border: 1px solid #ddd;"><strong>${item.itemTitle}</strong>`;
    if (item.purchase.selectedVariations && Object.keys(item.purchase.selectedVariations).length > 0) {
      details += '<br><small style="color: #666;">';
      Object.keys(item.purchase.selectedVariations).forEach(varType => {
        const variation = item.purchase.selectedVariations[varType];
        details += `${varType}: ${variation.name || variation}`;
        if (variation.description) details += ` (${variation.description})`;
        details += '<br>';
      });
      details += '</small>';
    }
    details += '</td>';
    details += `<td style="padding: 10px; border: 1px solid #ddd;">${item.qty}</td>`;
    details += `<td style="padding: 10px; text-align: right; border: 1px solid #ddd;">$${itemCost.toFixed(2)}`;
    if (shippingCost > 0) {
      details += `<br><small style="color: #666;">+ $${shippingCost.toFixed(2)} shipping</small>`;
    }
    details += '</td>';
    details += `<td style="padding: 10px; text-align: right; border: 1px solid #ddd;"><strong>$${subtotal.toFixed(2)}</strong></td>`;
    details += '</tr>';
  });
  
  details += '</tbody></table>';
  
  if (shipping && shipping.name) {
    details += '<div style="margin: 20px 0; padding: 15px; background-color: #f9f9f9; border-left: 4px solid #4CAF50;">';
    details += '<h3 style="margin-top: 0;">Shipping Address:</h3>';
    details += `<p><strong>${shipping.name}</strong></p>`;
    if (shipping.street) details += `<p>${shipping.street}</p>`;
    if (shipping.city) details += `<p>${shipping.city}`;
    if (shipping.state) details += `, ${shipping.state}`;
    if (shipping.zip) details += ` ${shipping.zip}`;
    if (shipping.city) details += '</p>';
    if (shipping.country) details += `<p>${shipping.country}</p>`;
    details += '</div>';
  }
  
  if (payment && payment["payment-type"]) {
    details += '<div style="margin: 20px 0; padding: 15px; background-color: #f9f9f9; border-left: 4px solid #2196F3;">';
    details += '<h3 style="margin-top: 0;">Payment Method:</h3>';
    details += `<p><strong>${payment["payment-type"]}</strong>`;
    if (payment.currency) details += ` (${payment.currency.toUpperCase()})`;
    details += '</p></div>';
  }
  
  return details;
}

// Helper function to get user email
async function getUserEmail(userId) {
  const store = window.$store;
  try {
    const email = await store.dispatch('query', { type: 'email', id: userId });
    return email;
  } catch (error) {
    console.error('Error getting user email:', error);
    // Fallback to auth email if available
    const auth = store.state.auth;
    if (auth && auth.email) return auth.email;
    return null;
  }
}

export default async function create(state, payload) {
  console.log("creating from payload", payload);
  
  // Get store references from window (needed for nested dispatches)
  const store = window.$store;
  const s = store.state;
  const { fb, fbBase, fbusersbase } = require('../util').default.fb();
  const [t, f] = [true, false];
  const version = s.app && s.app.version ? 
    (s.app.version.toString().startsWith('v') ? s.app.version : 'v' + s.app.version) : 
    'v1';
  const st = s;
  
  if (!s.auth.uid) {
    return (s.show.auth = t);
  } else if (payload.data && !payload.data.ownedBy) {
    payload.data.ownedBy = s.auth.uid;
  }

  const methods = {
    app: async (payload) => {
      payload.data.user = [s.auth.uid];
      payload.data.owner = s.auth.uid;
      payload.data.style = s.app.style;
      payload.data.advanced = s.app.advanced;
      
      payload.data.basics.database = location.hostname;
      return fb
        .firestore()
        .collection(encodeURIComponent(payload.data.basics.database))
        .doc(version)
        .set({ app: payload.data }, { merge: t })
        .then(() => {
          fb.firestore()
            .collection("apps")
            .doc(version)
            .set(
              { [encodeURIComponent(payload.data.basics.database)]: t },
              { merge: t }
            );
          store.commit('setStyle', s);
          store.commit("set", { keys: ["show", "reinit"], val: t });
          return payload.data.basics.database;
        });
    },
    fromdata: (payload) => {
      // TODO refactor, simplify, identify what it does really. what does it mean to create items from data?
      let model = payload.data.model,
        workingmodel = store.dispatch('validjson', model)
          ? JSON.parse(model)
          : model,
        list = [],
        determineinputtype = (example) => {
          if (
            (typeof example == "string" && store.dispatch('validjson', example)) ||
            (typeof example == "object" && !Array.isArray(example))
          ) {
            return ["form", t];
          }

          if (
            (typeof example == "string" && store.dispatch('validjson', example)) ||
            (typeof example == "object" && Array.isArray(example))
          ) {
            return ["select", t];
          }

          if (
            typeof example == "string" &&
            !store.dispatch('validjson', example) &&
            isNaN(example)
          ) {
            return ["input", "text"];
          }

          if (typeof example === "number" || !isNaN(example)) {
            return ["input", "number"];
          }

          if (typeof example === "boolean") {
            return ["check", "true"];
          }
        };
      if (Array.isArray(workingmodel)) {
        list = JSON.parse(JSON.stringify(workingmodel));
        workingmodel = list[0];
      }
      if (store.state.app.itemTypes.indexOf(payload.data.name) > -1) {
        list.length > 0 &&
          store.dispatch('create', {
            type: payload.data.name,
            data: list,
            multiple: t,
          });
      } else {
        let item = {
          comments: f,
          crowdallowed: f,
          forms: [
            {
              name: "basics",
              description: "The basic bits of an item.",
              inputs: [
                {
                  inputType: "input",
                  label: "title",
                  type: "text",
                  placeholder: "title",
                  default: "",
                  priority: 1,
                },
                {
                  inputType: "textarea",
                  label: "description",
                  type: "text",
                  placeholder: "Description",
                  default: "",
                  priority: 2,
                },
                {
                  inputType: "file",
                  label: "image",
                  type: "image",
                  placeholder: "Image Url",
                  default: "",
                  priority: 3,
                },
                {
                  inputType: "tags",
                  label: "tags",
                  type: "tags",
                  placeholder: "tags",
                  default: [],
                  priority: 4,
                },
              ],
            },
            {
              description: "",
              inputs: [],
              name: "info",
            },
          ],
          name: payload.data.name,
          reputation: f,
          type: "asset",
        };
        item.forms[1].inputs = Object.entries(workingmodel).map((x) => {
          return {
            default: "",
            label: x[0],
            inputType: determineinputtype(x[1])[0],
            placeholder: x[0],
            type: determineinputtype(x[1])[1],
            if: [],
          };
        });
        s.app.basics.items.push(item);
        s.app.itemTypes.push(payload.data.name);
        s.app.itemTypes = [...new Set(s.app.itemTypes)];
        store.dispatch('update', { type: "app", data: s.app }).then(() => {
          list.length > 0 &&
            store.dispatch('create', {
              type: payload.data.name,
              data: list,
              multiple: t,
            });
        });
      }
    },
    msg: (payload) => {
      payload.data.id = fbBase.collection("msgs").doc().id;
      payload.data.topic = payload.data.topic || "Set topic?";
      payload.data.owner = s.auth.uid;
      const msgBatch = fb.firestore().batch();
      msgBatch
        .set(fbBase.collection("msgs").doc(payload.data.id), payload.data, {
          merge: t,
        })
        .commit()
        .then(() => {
          payload.data.users.forEach((user) => {
            var docRef = fbBase.collection("user").doc(user);
            docRef.get().then((userData) => {
              var user = userData.data();
              user.msgs = user.msgs || {};
              if (user === s.auth.uid) {
                user.msgs[payload.data.id] = t;
              } else {
                user.msgs[payload.data.id] = "unseen";
              }
              fb.firestore()
                .batch()
                .set(docRef, user, { merge: t })
                .commit()
                .then(() => {
                  // Update state directly instead of refetching
                  if (s.msgs) {
                    Vue.set(s.msgs, payload.data.id, payload.data);
                  }
                  store.dispatch('query', { type: "msgs" });
                });
            });
          });
        });
      return payload.data.id;
    },
    notification: async (payload) => {
      payload.data.id = payload.data.id || fbBase.collection("notifications").doc().id;
      payload.data.timestamp = payload.data.timestamp || Date.now();
      const notifyBatch = fb.firestore().batch();
      
      // Clean function to remove functions and ensure serializable data
      const cleanForFirestore = (obj) => {
        if (obj === null || obj === undefined) return null;
        if (typeof obj === 'function') return null;
        if (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean') return obj;
        if (Array.isArray(obj)) {
          return obj.map(cleanForFirestore).filter(x => x !== null && x !== undefined);
        }
        if (typeof obj === 'object') {
          const cleaned = {};
          Object.keys(obj).forEach(key => {
            const value = obj[key];
            if (typeof value !== 'function' && value !== undefined) {
              const cleanedValue = cleanForFirestore(value);
              if (cleanedValue !== null && cleanedValue !== undefined) {
                cleaned[key] = cleanedValue;
              }
            }
          });
          return cleaned;
        }
        return obj;
      };
      
      // Ensure all required fields are defined and serializable
      const notificationData = cleanForFirestore({
        id: payload.data.id,
        timestamp: payload.data.timestamp,
        actions: Array.isArray(payload.data.actions) ? payload.data.actions : [],
        subMessage: String(payload.data.subMessage || ""),
        from: String(payload.data.from || 'system'),
        link: payload.data.link ? String(payload.data.link) : null,
        message: String(payload.data.message || ''),
        to: Array.isArray(payload.data.to) ? payload.data.to : [],
        type: typeof payload.data.type === 'string' ? payload.data.type : 'system',
      });
      
      return notifyBatch
        .set(fbBase.collection("notifications").doc(notificationData.id), notificationData, {
          merge: t,
        })
        .commit()
        .then(() =>
          notificationData.to.forEach((user) => {
            var docRef = fbBase.collection("user").doc(user);
            docRef.get().then((userData) => {
              var user = userData.data();
              user.notifications = user.notifications || {};
              Vue.set(user.notifications, notificationData.id, t);
              fb.firestore().batch().update(docRef, user).commit();
            });
          })
        );
    },
    comments: async (payload) => {
      console.log("comment", { payload });
      payload.comment.id = fbBase.collection("comments").doc().id;
      payload.comment.ownedBy = s.auth.uid;
      const commentBatch = fb.firestore().batch();
      return commentBatch
        .set(
          fbBase.collection("comments").doc(payload.id),
          { [payload.comment.id]: payload.comment },
          {
            merge: t,
          }
        )
        .commit()
        .then(() => {
          store.dispatch('query', { type: "comments", id: payload.comment.id });
          return "Comment posted";
        });
    },
    reputation: async (payload) => {
      var newId = fbBase.collection("reputation").doc().id;
      const reputation = {
        [newId]: {
          ref: payload.id,
          feedback: payload.data,
          user: s.auth.uid,
          timestamp: Date.now(),
          type: "reputation",
          rId: newId,
          rType: payload.data.fType || "Untyped",
        },
      };
      // TODO set user activity on user something
      const reputationBatch = fb.firestore().batch();
      return reputationBatch
        .set(fbBase.collection("reputation").doc(payload.data.id), reputation, {
          merge: true,
        })
        .commit()
        .then(async () => {
          if (
            typeof s.user[s.auth.uid].userreps !== "object" ||
            Array.isArray(s.user[s.auth.uid].userreps)
          ) {
            s.user[s.auth.uid].userreps = {};
          }
          console.log(s.user[s.auth.uid].userreps);
          store.commit("set", {
            keys: ["user", s.auth.uid, "userreps", payload.id],
            val: newId,
          });
          store.dispatch("update", {
            type: "user",
            data: s.user[s.auth.uid],
          });

          var tRef = fbBase
            .collection("reputation")
            .doc(payload.data.id)
            .collection("meta")
            .doc("stats");

          return fb
            .firestore()
            .runTransaction(function (t) {
              return t.get(tRef).then(function (sfDoc) {
                if (!sfDoc.exists) {
                  return t.set(
                    tRef,
                    { count: 1, quality: payload.data.quality },
                    { merge: true }
                  );
                }
                var newCount = sfDoc.data().count + 1;
                var totalQuality =
                  Number(sfDoc.data().quality) + Number(payload.data.quality);
                return t.set(
                  tRef,
                  { count: newCount, quality: totalQuality },
                  { merge: true }
                );
              });
            })
            .then(function () {
              store.dispatch('query', { type: "reputation", id: payload.data.id });
              return reputation;
            })
            .catch(function (error) {
              console.log("Transaction failed: ", error);
            });
        });
    },
    order: async (payload) => {
      // TODO refactor order mechanism to be cleaner and simpler
      var cart = payload.cart || s.user[s.auth.uid].cart;
      var orderDate = Date.now();
      var shipping = payload.data["Shipping"];
      var payment = payload.data["Payment"];
      
      // Check if this is an update to an existing order
      var isUpdate = store.state.pendingOrderUpdate && store.state.pendingOrderUpdate.orderId;
      var orderId = isUpdate ? store.state.pendingOrderUpdate.orderId : fbBase.collection("orders").doc().id;
      var sellers = {};
      var fullOrder = {
        id: orderId,
        complete: f,
        paid: f,
        shipping: shipping,
        items: [],
        total: 0,
        timestamp: orderDate,
        payments: [], // Array to track all Stripe webhook events for this order
      };
      // Track highest shipping per seller
      var sellerShipping = {};
      
      Object.keys(cart).forEach((order) => {
        if (cart) {
          var orderArr = cart[order];
          var orderObj = {
            type: orderArr[0],
            iitemid: orderArr[1],
            purchase: orderArr[2],
            qty: orderArr[3],
            seller: orderArr[4],
            itemTitle: orderArr[5],
            status: "new-order", // ['new-order', 'processing', 'shipped', 'delivered', 'order-error']
            shipping: shipping,
            orderId,
            buyer: store.state.auth.uid || "anon",
            timestamp: orderDate,
          };
          // Calculate item subtotal without shipping
          var subTotal = orderObj.qty * orderObj.purchase.cost;
          
          // Track highest shipping per seller
          var itemShipping = typeof orderObj.purchase["shipping and handling"] === "number"
            ? orderObj.purchase["shipping and handling"]
            : 0;
          if (itemShipping > 0) {
            var sellerId = orderObj.seller;
            if (!sellerShipping[sellerId] || itemShipping > sellerShipping[sellerId]) {
              sellerShipping[sellerId] = itemShipping;
            }
          }
          
          sellers[orderObj.seller] = sellers[orderObj.seller] || [];
          sellers[orderObj.seller].push(orderObj);
          fullOrder.items.push(orderObj);
          fullOrder.total = +fullOrder.total + +subTotal;
        }
      });
      
      // Add shipping once per seller (highest value)
      Object.keys(sellerShipping).forEach((sellerId) => {
        fullOrder.total = +fullOrder.total + +sellerShipping[sellerId];
      });

      const orderBatch = fb.firestore().batch();
      fullOrder.total = Number(fullOrder.total).toFixed(2);
      
      // Use database from app.basics.database to match webhook path structure
      const database = s.app?.basics?.database || window.location.hostname || 'xmbl';
      const encodedDatabase = encodeURIComponent(database);
      const orderRef = fb.firestore()
        .collection(encodedDatabase)
        .doc(version)
        .collection("orders")
        .doc(orderId);
      
      return orderBatch
        .set(orderRef, fullOrder, {
          merge: t,
        })
        .commit()
        .then(() => {
          console.log(isUpdate ? "order update success" : "order creation success", payment["payment-type"]);
          
          // Clear pending order update flag
          if (isUpdate) {
            store.state.pendingOrderUpdate = null;
          }
          
          store.dispatch('transact', { orderId, payment, fullOrder });
          
          // Only add orderId to orders array if it's a new order
          if (!isUpdate) {
            s.user[s.auth.uid].orders = s.user[s.auth.uid].orders || [];
            if (!s.user[s.auth.uid].orders.includes(orderId)) {
              s.user[s.auth.uid].orders.push(orderId);
            }
          }
          
          Vue.set(s.user[s.auth.uid], "cart", {});
          Vue.set(s.counts, "cart", 0);
          store.state.show.cart = f;
          
          // Only update the fields that changed to avoid Vue reactivity and circular reference issues
          const updateData = {
            orders: Array.isArray(s.user[s.auth.uid].orders) ? s.user[s.auth.uid].orders.filter(x => x !== undefined) : [],
            cart: {},
          };
          
          fb.firestore()
            .batch()
            .update(
              fbBase.collection("user").doc(s.auth.uid),
              updateData
            )
            .commit()
            .catch(err => {
              console.error('Error updating user:', err);
            });
          Object.keys(sellers).forEach((seller) => {
            var docRef = fbBase.collection("user").doc(seller);
            docRef.get().then((userData) => {
              var user = userData.data();
              user.sales = user.sales || {};
              user.sales[orderId] = sellers[seller];
              fb.firestore()
                .batch()
                .set(docRef, user, { merge: t })
                .commit()
                .then(() => {
                    // Only send notifications and emails for new orders, not updates
                    if (!isUpdate) {
                    store.dispatch('notify', {
                      type: "sale",
                      message: "Order created",
                      to: [sellers[seller][0].seller],
                      from: sellers[seller][0].buyer || store.state.auth.uid,
                      link: `/sales/${sellers[seller][0].orderId}`,
                      id: sellers[seller][0].orderId,
                      subMessage: `Order ID: ${sellers[seller][0].orderId}`,
                      actions: ["fulfill", "send thank you and request pay"]
                    });
                    store.dispatch('notify', {
                      type: "order",
                      message: `Order ${sellers[seller][0].orderId} created`,
                      to: [sellers[seller][0].buyer],
                      from: sellers[seller][0].seller || store.state.auth.uid,
                      link: `/orders/${sellers[seller][0].orderId}`,
                      id: sellers[seller][0].orderId
                    });
                    
                    // Send email notifications to buyer and seller when order is created
                    // Order link will be updated when payment is confirmed
                    const orderLink = `${window.location.origin}/orders/${sellers[seller][0].orderId}`;
                    const saleLink = `${window.location.origin}/sales/${sellers[seller][0].orderId}`;
                    const appName = (store.state.app && store.state.app.basics && store.state.app.basics["app name"]) || 'XMBL';
                    const orderDetails = formatOrderDetails(sellers[seller], shipping, payment);
                    const paymentStatus = payment["payment-type"] === "Credit" ? "Pending Payment" : payment["payment-type"] || "Pending";
                    
                    // Email to seller
                    getUserEmail(sellers[seller][0].seller).then(sellerEmail => {
                      if (sellerEmail) {
                        const sellerHtml = `
                          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                            <h1 style="color: #4CAF50;">New Sale Received</h1>
                            <p>You have received a new order on ${appName}!</p>
                            <div style="background-color: #f5f5f5; padding: 15px; margin: 20px 0; border-radius: 5px;">
                              <p style="margin: 5px 0;"><strong>Order ID:</strong> ${sellers[seller][0].orderId}</p>
                              <p style="margin: 5px 0;"><strong>Order Date:</strong> ${new Date(orderDate).toLocaleString()}</p>
                              <p style="margin: 5px 0;"><strong>Total Amount:</strong> <strong style="color: #4CAF50; font-size: 1.2em;">$${fullOrder.total}</strong></p>
                              <p style="margin: 5px 0;"><strong>Payment Status:</strong> ${paymentStatus}</p>
                            </div>
                            ${orderDetails}
                            <div style="margin: 30px 0; text-align: center;">
                              <a href="${saleLink}" style="background-color: #4CAF50; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; display: inline-block;">View Order Details</a>
                            </div>
                          </div>
                        `;
                        const sellerText = `New Sale - Order ${sellers[seller][0].orderId}\n\nYou have received a new order on ${appName}!\n\nOrder ID: ${sellers[seller][0].orderId}\nOrder Date: ${new Date(orderDate).toLocaleString()}\nTotal: $${fullOrder.total}\nPayment Status: ${paymentStatus}\n\nItems:\n${sellers[seller].map(item => `- ${item.qty}x ${item.itemTitle} - $${(parseFloat(item.purchase.cost) * item.qty).toFixed(2)}`).join('\n')}\n\nView order: ${saleLink}`;
                        sendEmail(sellerEmail, `${appName} - New Sale - Order ${sellers[seller][0].orderId}`, sellerHtml, sellerText, appName).catch(err => {
                          // Silently handle email errors - don't break the order creation
                          console.log('Email send failed (non-critical):', err);
                        });
                      }
                    });
                    
                    // Email to buyer
                    getUserEmail(sellers[seller][0].buyer).then(buyerEmail => {
                      if (buyerEmail) {
                        const buyerHtml = `
                          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                            <h1 style="color: #4CAF50;">Order Confirmation</h1>
                            <p>Thank you for your order on ${appName}!</p>
                            <div style="background-color: #f5f5f5; padding: 15px; margin: 20px 0; border-radius: 5px;">
                              <p style="margin: 5px 0;"><strong>Order ID:</strong> ${sellers[seller][0].orderId}</p>
                              <p style="margin: 5px 0;"><strong>Order Date:</strong> ${new Date(orderDate).toLocaleString()}</p>
                              <p style="margin: 5px 0;"><strong>Total Amount:</strong> <strong style="color: #4CAF50; font-size: 1.2em;">$${fullOrder.total}</strong></p>
                              <p style="margin: 5px 0;"><strong>Payment Status:</strong> ${paymentStatus}</p>
                            </div>
                            ${orderDetails}
                            <div style="margin: 30px 0; text-align: center;">
                              <a href="${orderLink}" style="background-color: #4CAF50; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; display: inline-block;">View Order Details</a>
                            </div>
                          </div>
                        `;
                        const buyerText = `Order Confirmation - Order ${sellers[seller][0].orderId}\n\nThank you for your order on ${appName}!\n\nOrder ID: ${sellers[seller][0].orderId}\nOrder Date: ${new Date(orderDate).toLocaleString()}\nTotal: $${fullOrder.total}\nPayment Status: ${paymentStatus}\n\nItems:\n${sellers[seller].map(item => `- ${item.qty}x ${item.itemTitle} - $${(parseFloat(item.purchase.cost) * item.qty).toFixed(2)}`).join('\n')}\n\nView order: ${orderLink}`;
                        sendEmail(buyerEmail, `${appName} - Order Confirmation - Order ${sellers[seller][0].orderId}`, buyerHtml, buyerText, appName).catch(err => {
                          // Silently handle email errors - don't break the order creation
                          console.log('Email send failed (non-critical):', err);
                        });
                      }
                    });
                  }
                });
            });
          });
          return fullOrder;
        });
    },
    value: async (payload) => {
      let wb = fb.firestore().batch();
      wb.set(
        fbusersbase.collection(s.auth.uid).doc("values"),
        { [s.app.basics.database]: { [payload.key]: payload.value } },
        { merge: t }
      );
      return wb
        .set(
          fbBase
            .collection("user")
            .doc(s.auth.uid)
            .collection(payload.datatype)
            .doc(payload.datatype),
          { [payload.key]: payload.value },
          { merge: t }
        )
        .commit()
        .then(() => {
          s.value[payload.datatype] = s.value[payload.datatype] || {};
          s.value[payload.datatype][payload.key] =
            s.value[payload.datatype][payload.key] || {};
          Vue.set(s.value[payload.datatype], payload.key, payload.value);
          // return store.dispatch('query', {type: payload.type, id: payload.id, itemType: payload.itemType})
        });
    },
    item: async (payload) => {
      let itemconfig = s.app.basics.items.filter(
        (x) => x.name === payload.type
      )[0];
      if (
        itemconfig &&
        itemconfig.hooks &&
        itemconfig.hooks.create &&
        (itemconfig.hooks.create.fun || itemconfig.hooks.create.get)
      ) {
        let name = itemconfig.hooks.create.fun
          ? itemconfig.hooks.create.fun.name
          : itemconfig.hooks.create.get.name;
        store.dispatch("run", {
          from: "item create " + payload.type,
          if: itemconfig.hooks.create.if || [],
          fun: s.app.advanced.functions[name],
          payload:
            itemconfig.hooks.create.getpayload ||
            itemconfig.hooks.create.payload ||
            {},
        });
      }
      var userRef = fbBase.collection("user").doc(s.auth.uid);
      var wb = fb.firestore().batch();
      let setdata = async (entry, user) => {
        console.log("setdata()", { entry, user });
        let wellformed = {};
        // Housekeeping
        Object.keys(entry).forEach((key) => {
          if (!payload.data[key]) delete payload.data[key];
        });
        const date = Date.now();
        wellformed.created = date;

        // Check if item type exists and has forms
        const itemTypeIndex = s.app.itemTypes
          ? s.app.itemTypes.indexOf(payload.type)
          : -1;
        if (
          itemTypeIndex >= 0 &&
          s.app.basics.items[itemTypeIndex] &&
          s.app.basics.items[itemTypeIndex].forms
        ) {
          s.app.basics.items[itemTypeIndex].forms.map((section) => {
            wellformed[section.name] = {};
            section.inputs.map(
              (x) =>
                (wellformed[section.name][x.label] =
                  entry[section.name] && entry[section.name][x.label]
                    ? entry[section.name][x.label]
                    : entry[x.label] || "")
            );
          });
        } else {
          // Fallback: create basic structure from payload data
          wellformed.basics = entry.basics || entry.basics || {};
          wellformed.purchases = entry.purchases || {};
          wellformed.social = entry.social || {};
          wellformed.media = entry.media || {};
        }

        wellformed.user = st.auth.uid;
        wellformed.type = payload.type;
        if (!wellformed.basics || !wellformed.basics.title) {
          wellformed.basics = wellformed.basics || {};
          wellformed.basics.title =
            entry.title || entry.name || entry.id || wellformed.created;
        }
        const hashes = await store
          .dispatch("t", {
            type: s.lexicon.create.item,
            payload: wellformed,
          })
          .then((h) => h);
        console.log("hashes", hashes);
        var document = fbBase.collection(payload.type).doc(hashes[1] || "");
        console.log("create document", document);
        user.items[payload.type].push(document.id);
        user.items[payload.type] = [...new Set(user.items[payload.type])];
        store.dispatch("indexer", [wellformed, document.id, payload.type]);
        store.commit("point", {
          header: payload.type + " created",
          text: document.id,
          link: "/i/" + payload.type + "/" + document.id,
          sticky: f,
          speed: 0,
        });
        wellformed.hash = hashes[0];
        wellformed.id = document.id;
        wb.set(document, wellformed);
        return document;
      };

      return userRef.get().then(async (userData) => {
        var updateObj = userData.data();
        if (!updateObj.items) {
          updateObj.items = {};
        }
        if (!updateObj.items[payload.type]) {
          updateObj.items[payload.type] = [];
        } else if (
          updateObj.items[payload.type] &&
          !Array.isArray(updateObj.items[payload.type])
        ) {
          var newArr = [];
          Object.keys(updateObj.items[payload.type]).forEach((objKey) => {
            newArr.push(objKey);
          });
          updateObj.items[payload.type] = newArr;
        } else {
          console.log("Unknown entry", payload);
        }

        // Checks for an array of items to map and set, else sets item
        console.log(updateObj);
        payload.multiple
          ? payload.data.map(async (entry) => {
              await setdata(entry, updateObj).then((d) => d);
            })
          : await setdata(payload.data, updateObj).then((d) => d);

        wb.set(
          fbusersbase.collection(s.auth.uid).doc("assets"),
          { [s.app.basics.database]: updateObj },
          { merge: t }
        );
        wb.set(fbBase.collection("user").doc(s.auth.uid), updateObj, {
          merge: t,
        }).commit();
        payload.data.secret && Vue.set(st, "secretCode", payload.data.id);
        payload.data.secret &&
          setTimeout(() => {
            Vue.set(st, "secretCode", "");
          }, 10000);
        // mu.closeAll(store.state)
        return payload.data;
      });
    },
  };
  const isItem = !Object.keys(methods).includes(payload.type);
  return methods[isItem ? "item" : payload.type](payload); 
}

